import * as React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { translate } from '~l10n/client';

import { UIButton } from '~shared/components/buttons';
import { Dialog } from '~shared/components/Dialog';
import { Divider } from '~shared/components/Divider';
import { useAutoFocus } from '~shared/components/helpers';
import { Inline } from '~shared/components/Inline';
import TextInput from '~shared/components/TextInput';
import { useFolderName } from '~teacher/components/quizzes/RenameFolderDialog/useFolderName';
import { useSaveNewFolder } from '~teacher/components/quizzes/RenameFolderDialog/useSaveNewFolder';

interface Props {
  renameFolderId: number;
  initialRenameFolderName: string;
  onCloseRenameFolder: () => void;
}

export function RenameFolderDialog({
  renameFolderId,
  initialRenameFolderName,
  onCloseRenameFolder,
}: Props) {
  const queryClient = useQueryClient();

  const [folderName, setFolderName] = useFolderName(initialRenameFolderName);

  const { isLoading, saveNewFolderName } = useSaveNewFolder({
    folderName: folderName,
    oldName: initialRenameFolderName,
    renameFolderId: renameFolderId,
    queryClient: queryClient,
    onCloseRenameFolder: onCloseRenameFolder,
  });

  const isRenameFolderOpen = renameFolderId !== null;
  const autoFocusOnOpenCallback = useAutoFocus();

  return (
    <Dialog
      onClose={onCloseRenameFolder}
      id="add-folder-modal"
      open={isRenameFolderOpen}
      title={translate('Rename Folder')}
      closeLabel={translate('Cancel')}
    >
      <div className="mt-5 mb-5">
        <Divider />
      </div>
      <div className="text-base leading-8">
        <label htmlFor="folder-name">{translate('Folder name')}</label>
        <TextInput
          maxLength={255}
          ref={autoFocusOnOpenCallback}
          id="folder-name"
          value={folderName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFolderName(event.target.value)
          }
          className="mt-3"
        />
      </div>
      <div className="pt-8">
        <Inline align="right">
          <UIButton isDisabled={isLoading} onClick={onCloseRenameFolder}>
            {translate('Cancel')}
          </UIButton>
          <UIButton
            isDisabled={isLoading}
            appearance="primary"
            onClick={saveNewFolderName}
          >
            {translate('Rename')}
          </UIButton>
        </Inline>
      </div>
    </Dialog>
  );
}
